import type { DomainItem } from '@/types';

import React from 'react';
import { RiGlobalLine, RiAddLine, RiEyeLine } from '@remixicon/react';
import {
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Skeleton,
  Button,
  Tooltip,
  useDisclosure
} from '@heroui/react';
import { Link } from 'react-router-dom';

import { AddDomainForm } from './AddDomainForm';
import { truncateText } from '@/utils/helpers';
import { DomainCell } from '@/components/shared/DomainCell';

interface DomainsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  domains: DomainItem[];
  isLoading: boolean;
  organizationName?: string;
  organizationId?: number;
  onDomainsChange: () => void;
}

export function DomainsModal({
  isOpen,
  onOpenChange,
  domains,
  isLoading,
  organizationName,
  organizationId,
  onDomainsChange
}: DomainsModalProps) {
  const columns = [
    { name: 'Domain', uid: 'domain' },
    { name: 'Title', uid: 'title' },
    { name: 'Status', uid: 'status' },
    { name: 'Domain Event Cap', uid: 'token_limit' },
    { name: 'Actions', uid: 'actions', align: 'end' }
  ];

  // Add domain modal state
  const { isOpen: isAddDomainOpen, onOpen: onAddDomainOpen, onOpenChange: onAddDomainOpenChange } = useDisclosure();

  const renderCell = React.useCallback((domain: DomainItem, columnKey: React.Key) => {
    switch (columnKey) {
      case 'domain':
        return <DomainCell domain={domain} linked />;
      case 'title':
        return (
          <Tooltip content={domain.title}>
            <span>{truncateText(domain.title, 30)}</span>
          </Tooltip>
        );
      case 'status':
        const isVerified = domain.status === 'verified';
        const color = isVerified ? 'success' : 'warning';
        const label = isVerified ? 'Verified' : 'Pending';
        return (
          <Chip color={color} size="sm" variant="flat">
            {label}
          </Chip>
        );
      case 'token_limit':
        return domain.token_limit != null ? (
          <span>{Number(domain.token_limit).toLocaleString()}</span>
        ) : (
          <span className="text-default-500">Unlimited</span>
        );
      case 'actions':
        return (
          <div className="flex items-center justify-end gap-2">
            <Tooltip content="View domain details">
              <Button
                as={Link}
                to={`/domains?domain=${domain.domain}`}
                isIconOnly
                color="primary"
                size="sm"
                variant="light"
              >
                <RiEyeLine className="h-4 w-4" />
              </Button>
            </Tooltip>
          </div>
        );
      default:
        return null;
    }
  }, []);

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={onOpenChange} size="2xl">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex w-full items-center gap-4">
                <span>{organizationName ? `${organizationName}'s Domains` : 'Workspace Domains'}</span>
                <Button
                  color="primary"
                  size="sm"
                  variant="flat"
                  startContent={<RiAddLine className="h-4 w-4" />}
                  onPress={onAddDomainOpen}
                >
                  Add Domain
                </Button>
              </div>
            </ModalHeader>
            <ModalBody>
              <div className="max-h-[400px] overflow-y-auto">
                {isLoading ? (
                  // Loading skeleton with table structure
                  <Table
                    aria-label="Domains table loading"
                    removeWrapper
                    classNames={{
                      base: 'max-h-[400px] overflow-y-auto'
                    }}
                  >
                    <TableHeader columns={columns}>
                      {(column) => (
                        <TableColumn key={column.uid} align={column.uid === 'actions' ? 'end' : 'start'}>
                          {column.name}
                        </TableColumn>
                      )}
                    </TableHeader>
                    <TableBody
                      items={Array(3)
                        .fill(null)
                        .map((_, index) => ({ id: `skeleton-${index}` }))}
                    >
                      {(item) => (
                        <TableRow key={item.id}>
                          {(columnKey) => (
                            <TableCell>
                              <Skeleton className="h-4 w-full rounded-lg" />
                            </TableCell>
                          )}
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                ) : (
                  // Actual table with data
                  <Table
                    aria-label="Domains table"
                    removeWrapper
                    classNames={{
                      base: 'max-h-[400px] overflow-y-auto'
                    }}
                  >
                    <TableHeader columns={columns}>
                      {(column) => (
                        <TableColumn key={column.uid} align={column.uid === 'actions' ? 'end' : 'start'}>
                          {column.name}
                        </TableColumn>
                      )}
                    </TableHeader>
                    <TableBody emptyContent="No domains found" items={domains}>
                      {(item) => (
                        <TableRow key={item.id}>
                          {(columnKey) => <TableCell>{renderCell(item, columnKey)}</TableCell>}
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>

      {/* Add Domain Form */}
      {organizationId && (
        <AddDomainForm
          isOpen={isAddDomainOpen}
          onOpenChange={onAddDomainOpenChange}
          organizationId={organizationId}
          onSuccess={onDomainsChange}
        />
      )}
    </Modal>
  );
}
